/**
 * Cumulative feedback editor (D8): 3 strength + 3 development slots, each with a
 * competency select, the statement being fed back on, and free-text comments.
 * "Generate draft" fills the slots from the cross-session narrator.
 */
import React from 'react';
import { ActivityIndicator, Text, View } from 'react-native';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import type { IcfCompetencyCode } from '@/data/icfFormStatements';
import type { CrfCumulativeFeedback, CrfFeedbackSlot } from '@/types/mentorEngagements';
import { ActionButton, SegmentedField, TextField, mentorStyles } from './shared';

const SLOTS_PER_SIDE = 3;
const COMPETENCIES: IcfCompetencyCode[] = ['C3', 'C4', 'C5', 'C6', 'C7', 'C8'];

function emptySlot(): CrfFeedbackSlot {
  return { competency_code: null, statement_ids: [], statement_for_feedback: '', feedback_comments: '' };
}

function padSlots(slots: CrfFeedbackSlot[] | undefined): CrfFeedbackSlot[] {
  const filled = [...(slots || [])].slice(0, SLOTS_PER_SIDE);
  while (filled.length < SLOTS_PER_SIDE) filled.push(emptySlot());
  return filled;
}

interface CumulativeFeedbackEditorProps {
  feedback: CrfCumulativeFeedback | null;
  hasDraft: boolean;
  generating: boolean;
  readOnly?: boolean;
  onChange: (feedback: CrfCumulativeFeedback) => void;
  onGenerate: () => void;
}

export function CumulativeFeedbackEditor({
  feedback,
  hasDraft,
  generating,
  readOnly = false,
  onChange,
  onGenerate,
}: CumulativeFeedbackEditorProps) {
  const { t } = useTranslation();
  const strengths = padSlots(feedback?.strengths);
  const development = padSlots(feedback?.development);

  const updateSlot = (
    side: 'strengths' | 'development',
    index: number,
    patch: Partial<CrfFeedbackSlot>,
  ) => {
    const next: CrfCumulativeFeedback = { strengths: [...strengths], development: [...development] };
    next[side][index] = { ...next[side][index], ...patch };
    onChange(next);
  };

  const renderSide = (side: 'strengths' | 'development', slots: CrfFeedbackSlot[], titleKey: string) => (
    <View style={{ gap: 12, flexGrow: 1, flexBasis: 280, minWidth: 0, maxWidth: '100%' }}>
      <Text style={[mentorStyles.panelTitle, { fontSize: 15 }]}>{t(titleKey)}</Text>
      {slots.map((slot, index) => (
        <View
          key={`${side}-${index}`}
          testID={`crf_feedback_${side}_${index}`}
          style={{
            gap: 8,
            padding: 12,
            borderRadius: 14,
            borderWidth: 1,
            borderColor: reviewDesk.line,
            backgroundColor: reviewDesk.cardMuted,
          }}
        >
          <SegmentedField
            label={t('mentorEngagements.crf.slotCompetency')}
            options={COMPETENCIES.map((code) => ({ value: code, label: code }))}
            value={slot.competency_code}
            onChange={(code) => updateSlot(side, index, { competency_code: code })}
            disabled={readOnly}
          />
          <TextField
            label={t('mentorEngagements.crf.slotStatement')}
            value={slot.statement_for_feedback}
            onChangeText={(value) => updateSlot(side, index, { statement_for_feedback: value })}
            editable={!readOnly}
          />
          <TextField
            label={t('mentorEngagements.crf.slotComments')}
            value={slot.feedback_comments}
            onChangeText={(value) => updateSlot(side, index, { feedback_comments: value })}
            multiline
            editable={!readOnly}
          />
        </View>
      ))}
    </View>
  );

  return (
    <View style={{ gap: 14 }} testID="crf_feedback_editor">
      <View style={[mentorStyles.row, { justifyContent: 'space-between' }]}>
        <View style={{ flexShrink: 1 }}>
          <Text style={mentorStyles.panelTitle}>{t('mentorEngagements.crf.feedbackTitle')}</Text>
          <Text style={mentorStyles.panelSubtitle}>{t('mentorEngagements.crf.feedbackSubtitle')}</Text>
        </View>
        <View style={mentorStyles.row}>
          {generating ? <ActivityIndicator color={reviewDesk.accent} /> : null}
          <ActionButton
            testID="crf_generate_narrative"
            label={hasDraft
              ? t('mentorEngagements.crf.regenerate')
              : t('mentorEngagements.crf.generateDraft')}
            icon="auto-awesome"
            onPress={onGenerate}
            disabled={readOnly || generating}
          />
        </View>
      </View>

      {generating ? (
        <Text style={mentorStyles.mutedText}>{t('mentorEngagements.crf.generating')}</Text>
      ) : null}

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
        {renderSide('strengths', strengths, 'mentorEngagements.crf.strengths')}
        {renderSide('development', development, 'mentorEngagements.crf.development')}
      </View>
    </View>
  );
}

export default CumulativeFeedbackEditor;
